'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Search, X, Menu, LogOut, User, Languages } from 'lucide-react'
import { useTheme } from '../theme'

// Navigation item interface
export interface NavItem {
  label: string
  href: string
  active?: boolean
}

// Language option interface
export interface LanguageOption {
  code: string
  label: string
  flag: string
}

// Firebase user interface
interface FirebaseUser {
  uid: string
  displayName: string | null
  photoURL: string | null
  email: string | null
}

export interface HeaderProps {
  /** Site logo URL */
  logoUrl: string
  /** Site title */
  siteTitle: string
  /** Current locale (e.g., 'en', 'fr', 'es') */
  locale?: string
  /** Navigation items */
  navItems: NavItem[]
  /** Available languages */
  languages?: LanguageOption[]
  /** Function to generate localized paths */
  getLocalizedPath?: (path: string, locale: string) => string
  /** Function to generate language switch URLs */
  getLanguageSwitchUrl?: (targetLocale: string, currentPath: string) => string
  /** Enable/disable search functionality */
  enableSearch?: boolean
  /** Search placeholder text */
  searchPlaceholder?: string
  /** Enable/disable Firebase authentication */
  enableAuth?: boolean
  /** Firebase auth callback */
  onAuthInit?: () => Promise<{
    onAuthStateChanged: (callback: (user: any) => void) => () => void
    signOut: () => Promise<void>
  }>
  /** Mobile breakpoint (default: 768px) */
  mobileBreakpoint?: number
  /** Logo size (default: { mobile: 40, desktop: 48 }) */
  logoSize?: { mobile: number; desktop: number }
  /** Additional className */
  className?: string
}

export function Header({
  logoUrl,
  siteTitle,
  locale = 'en',
  navItems,
  languages = [],
  getLocalizedPath = (path) => path,
  getLanguageSwitchUrl = (targetLocale) => `/${targetLocale}`,
  enableSearch = true,
  searchPlaceholder = 'Search...',
  enableAuth = false,
  onAuthInit,
  mobileBreakpoint = 768,
  logoSize = { mobile: 40, desktop: 48 },
  className = '',
}: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showMobileSearch, setShowMobileSearch] = useState(false)
  const [user, setUser] = useState<FirebaseUser | null>(null)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showLanguageMenu, setShowLanguageMenu] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const pathname = usePathname()
  const { theme, translations } = useTheme()

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < mobileBreakpoint)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [mobileBreakpoint])

  // Firebase auth initialization
  useEffect(() => {
    if (!enableAuth || !onAuthInit || typeof window === 'undefined') return

    let unsubscribe: (() => void) | undefined

    const initAuth = async () => {
      try {
        const auth = await onAuthInit()
        unsubscribe = auth.onAuthStateChanged((currentUser) => {
          setUser(currentUser as FirebaseUser | null)
        })
      } catch (error) {
        console.error('Error initializing auth:', error)
      }
    }

    // Defer auth init for performance
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => initAuth(), { timeout: 2000 })
    } else {
      setTimeout(() => initAuth(), 1000)
    }

    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [enableAuth, onAuthInit])

  // Handle logout
  const handleLogout = async () => {
    try {
      if (onAuthInit) {
        const auth = await onAuthInit()
        await auth.signOut()
        setUser(null)
        setShowUserMenu(false)
      }
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const isActivePage = (href: string) => {
    if (href === '/') {
      return pathname === '/' || pathname === `/${locale}` || pathname === `/${locale}/`
    }
    const localizedHref = getLocalizedPath(href, locale)
    return pathname.startsWith(localizedHref) || pathname.startsWith(href)
  }

  const getCurrentLanguage = () => {
    return languages.find((lang) => lang.code === locale) || languages[0]
  }

  // Theme colors
  const primaryColor = theme.colors.primary
  const secondaryColor = theme.colors.secondary || theme.colors.primary
  const headerBg = theme.components?.header?.background || `linear-gradient(to right, ${primaryColor}, ${primaryColor})`
  const borderColor = theme.components?.header?.borderColor || secondaryColor

  return (
    <header
      role="banner"
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-solid px-4 md:px-10 py-2 shadow-xl w-full ${className}`}
      style={{
        background: headerBg,
        borderBottomColor: `${borderColor}40`,
      }}
    >
      <div className="flex items-center justify-between whitespace-nowrap">
        <div className="flex items-center gap-4 md:gap-8">
          <Link
            href={getLocalizedPath('/', locale)}
            className="flex items-center gap-3 text-2xl font-bold text-white hover:opacity-90 transition-all duration-300 hover:scale-105"
          >
            <div
              className="relative rounded-full overflow-hidden shadow-lg ring-2 ring-white/30 transition-all duration-300"
              style={{
                width: isMobile ? logoSize.mobile : logoSize.desktop,
                height: isMobile ? logoSize.mobile : logoSize.desktop,
              }}
            >
              <Image
                src={logoUrl}
                alt={`${siteTitle} Logo`}
                fill
                className="object-contain"
                sizes={`(max-width: 768px) ${logoSize.mobile}px, ${logoSize.desktop}px`}
                priority
              />
            </div>
            <div className="text-xl md:text-2xl font-bold leading-tight tracking-[-0.015em] text-white">
              {siteTitle}
            </div>
          </Link>

          {!isMobile && (
            <nav role="navigation" aria-label="Primary navigation" className="flex items-center gap-2 ml-8">
              {navItems.map((item) => {
                const isActive = item.active !== undefined ? item.active : isActivePage(item.href)
                return (
                  <Link
                    key={item.href}
                    className={`relative px-4 py-2 text-base font-semibold transition-all duration-300 rounded-xl hover:bg-white/10 hover:backdrop-blur-sm ${
                      isActive
                        ? 'font-bold bg-white/15 shadow-lg backdrop-blur-sm'
                        : 'text-white'
                    }`}
                    style={{
                      color: isActive ? secondaryColor : '#ffffff',
                    }}
                    href={getLocalizedPath(item.href, locale)}
                  >
                    {item.label}
                    {isActive && (
                      <div
                        className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-6 h-1 rounded-full"
                        style={{ backgroundColor: secondaryColor }}
                      ></div>
                    )}
                  </Link>
                )
              })}
            </nav>
          )}
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          {/* Desktop Search */}
          {!isMobile && enableSearch && (
            <div className="relative w-80" role="search">
              <label htmlFor="search-input" className="sr-only">
                {searchPlaceholder}
              </label>
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" aria-hidden="true" />
              <input
                id="search-input"
                className="form-input w-full rounded-full border-2 border-white/20 bg-white/95 backdrop-blur-sm py-2.5 pl-12 pr-4 text-sm placeholder:text-gray-500 focus:outline-none transition-all duration-300 shadow-lg hover:shadow-xl hover:bg-white"
                style={{
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                }}
                placeholder={searchPlaceholder}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          )}

          {/* Desktop Language Switcher */}
          {!isMobile && languages.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-white/40"
                aria-label="Switch language"
              >
                <Languages className="w-5 h-5 text-white" />
                <span className="text-sm font-medium text-white hidden lg:block">
                  {getCurrentLanguage()?.flag}
                </span>
              </button>

              {showLanguageMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-200 py-1 z-50">
                  {languages.map((lang) => (
                    <Link
                      key={lang.code}
                      href={getLanguageSwitchUrl(lang.code, pathname)}
                      onClick={() => setShowLanguageMenu(false)}
                      className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors duration-200 flex items-center gap-3 ${
                        locale === lang.code ? 'font-bold' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      style={{
                        backgroundColor: locale === lang.code ? `${primaryColor}10` : 'transparent',
                        color: locale === lang.code ? primaryColor : '#374151',
                      }}
                    >
                      <span className="text-xl">{lang.flag}</span>
                      <span>{lang.label}</span>
                      {locale === lang.code && <span className="ml-auto">✓</span>}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Desktop User Menu */}
          {!isMobile && enableAuth && user && (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-white/40"
                aria-label="User account"
              >
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || 'User'}
                    className="w-8 h-8 rounded-full ring-2 ring-white/30"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: secondaryColor }}>
                    <User className="w-5 h-5" style={{ color: primaryColor }} />
                  </div>
                )}
                <span className="text-sm font-medium text-white hidden lg:block max-w-[100px] truncate">
                  {user.displayName || 'User'}
                </span>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">{translations.common?.signedInAs || 'Signed in as'}</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">{user.displayName}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-3 text-left text-sm font-medium text-red-600 hover:bg-red-50 transition-colors duration-200 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    {translations.common?.signOut || 'Sign out'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Mobile Actions */}
          {isMobile && (
            <>
              {enableSearch && (
                <button
                  className="p-2 rounded-full hover:bg-white/10 transition-all duration-300"
                  onClick={() => setShowMobileSearch(!showMobileSearch)}
                  aria-label="Search"
                >
                  <Search className="h-5 w-5 text-white" />
                </button>
              )}
              {languages.length > 0 && (
                <button
                  className="p-2 rounded-full hover:bg-white/10 transition-all duration-300 text-xl"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-label="Switch language"
                  title="Language"
                >
                  {getCurrentLanguage()?.flag}
                </button>
              )}
              <button
                className="p-2 rounded-full hover:bg-white/10 transition-all duration-300"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Menu"
              >
                {isMenuOpen ? <X className="h-5 w-5 text-white" /> : <Menu className="h-5 w-5 text-white" />}
              </button>
            </>
          )}
        </div>

        {/* Mobile Search Dropdown */}
        {isMobile && showMobileSearch && enableSearch && (
          <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 p-4 z-[100] shadow-lg">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                className="form-input w-full rounded-full border border-gray-200 bg-gray-50 py-3 pl-10 pr-4 text-base placeholder:text-gray-400 focus:outline-none"
                style={{
                  borderColor: '#e5e7eb',
                }}
                placeholder={searchPlaceholder}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {isMobile && isMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-xl z-[100]">
            {/* Mobile User Section */}
            {enableAuth && user && (
              <div className="px-6 py-4 border-b border-gray-200" style={{ background: `linear-gradient(to right, ${primaryColor}05, ${primaryColor}05)` }}>
                <div className="flex items-center gap-3 mb-3">
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={user.displayName || 'User'} className="w-12 h-12 rounded-full border-2" style={{ borderColor: `${primaryColor}50` }} />
                  ) : (
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: secondaryColor }}>
                      <User className="w-6 h-6" style={{ color: primaryColor }} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 mb-0.5">{translations.common?.signedInAs || 'Signed in as'}</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">{user.displayName}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                </div>
                <button onClick={handleLogout} className="w-full px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 font-medium text-sm">
                  <LogOut className="w-4 h-4" />
                  {translations.common?.signOut || 'Sign out'}
                </button>
              </div>
            )}

            {/* Mobile Language Switcher */}
            {languages.length > 0 && (
              <div className="px-6 py-4 border-b border-gray-200" style={{ background: `linear-gradient(to right, ${primaryColor}05, ${primaryColor}05)` }}>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Language</p>
                <div className="grid grid-cols-3 gap-2">
                  {languages.map((lang) => (
                    <Link
                      key={lang.code}
                      href={getLanguageSwitchUrl(lang.code, pathname)}
                      onClick={() => {
                        setShowLanguageMenu(false)
                        setIsMenuOpen(false)
                      }}
                      className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg transition-all duration-300 ${
                        locale === lang.code ? 'text-white shadow-lg scale-105' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                      }`}
                      style={{
                        backgroundColor: locale === lang.code ? primaryColor : 'white',
                      }}
                    >
                      <span className="text-2xl">{lang.flag}</span>
                      <span className="text-xs font-semibold">{lang.label}</span>
                      {locale === lang.code && <span style={{ color: secondaryColor }} className="text-xs">✓</span>}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Mobile Navigation */}
            <nav className="flex flex-col">
              {navItems.map((item) => {
                const isActive = item.active !== undefined ? item.active : isActivePage(item.href)
                return (
                  <Link
                    key={item.href}
                    className={`px-6 py-4 text-base font-semibold transition-colors border-b border-gray-100 active:bg-gray-100 ${
                      isActive ? 'font-bold bg-gray-50' : 'text-[#111318] hover:bg-gray-50'
                    }`}
                    style={{
                      color: isActive ? secondaryColor : '#111318',
                    }}
                    href={getLocalizedPath(item.href, locale)}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )
              })}
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}
